import {
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography,
} from '@material-ui/core'
import { Autocomplete } from '@material-ui/lab'
import { Button, CustomTooltip } from 'components/facets'
import { useExpansionData } from 'components/providers/CardDataProvider'
import { useUser } from 'components/providers/UserProvider'
import { useInventoryStore } from 'lib/hooks/useInventoryStore'
import { useStockStore } from 'lib/hooks/useStockStore'
import React, { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import {
  AnyYesNo,
  CardExtraAttribute,
  CARD_LANGUAGES,
  CONDITIONS,
  Expansion,
  Filters,
  IdGame,
} from 'shared'
import { SetState } from 'types'
import {
  BACKOFFICE_USER_STOCK,
  LISTING_AND_APPRAISAL,
  PRICING_BOT,
  STOCK_DISPLAY,
} from 'utils/constants'
import { v4 as uuid } from 'uuid'
import { shallow } from 'zustand/shallow'
import { Sort, sortFields, sortOptions, sortTypes } from '../StockDisplay/StockDisplayUtils'
import { Filter } from './Filter'

export const QUANTITY_FILTER_MAX = 24

export interface FilterPresenterProps {
  filters: Filters
  filtersKey: string
  defaultFilters: Filters
  areFiltersTouched: boolean
  setFiltersKey: SetState<string>
  setFilters: SetState<Filters>
  parentPage: string | undefined
}

export const FilterPresenter = React.memo(function FilterPresenter({
  filters,
  filtersKey,
  defaultFilters,
  areFiltersTouched,
  setFiltersKey,
  setFilters,
  parentPage,
}: FilterPresenterProps): JSX.Element {
  const expansions = useExpansionData()
  const { t } = useTranslation()
  // The backoffice inspects a user's stock with the same page: the stock-only filters belong there too
  const isStockPage = parentPage === STOCK_DISPLAY || parentPage === BACKOFFICE_USER_STOCK
  const activeStockType = useStockStore((state) => state.activeStockType)

  const [visibility, setVisibility, setSort, sort] = useInventoryStore(
    (s) => [s.visibility, s.setVisibility, s.setSort, s.sort],
    shallow
  )

  const { activeGame } = useUser()
  const [selectedSort, setSelectedSort] = useState(() => {
    const sortData = localStorage.getItem('sortArticles')
    if (sortData) {
      const lastSort = JSON.parse(sortData) as Sort
      return lastSort.by + '.' + lastSort.direction + '.' + lastSort.type
    } else return 'card.name.asc.string'
  })

  useEffect(() => {
    setSelectedSort(`${sort.by}.${sort.direction}.${sort.type}`)
  }, [sort])

  const expansionsActiveGame = expansions.filter((exp) => exp.idGame === activeGame.idGame)

  useEffect(() => {
    if (parentPage === PRICING_BOT) {
      const priceFilters = ['all', 'priceChanged', 'priceUnchanged', 'priceFailed']
      if (priceFilters.includes(visibility)) {
        const newValues: { [key: string]: AnyYesNo } = {}
        priceFilters.forEach((key) => {
          newValues[key] = key === visibility ? AnyYesNo.Yes : AnyYesNo.Any
        })
        handleChangeBulk(newValues)
      }
      return
    }

    let editedFilterValue = AnyYesNo.Any
    if (visibility === 'edited') {
      editedFilterValue = AnyYesNo.Yes
    } else if (visibility === 'non-edited') {
      editedFilterValue = AnyYesNo.No
    }
    handleChange('edited', editedFilterValue)
  }, [visibility])

  const handleChange = (name: string, newValue: unknown): void => {
    setFilters({ ...filters, [name]: newValue })
  }

  const handleQtyChange = (name: string, newValue: unknown): void => {
    const num = Number(newValue)
    const sanitized = !newValue || Number.isNaN(num) || num < 1 ? null : num
    setFilters({ ...filters, [name]: sanitized })
  }

  const handlePriceChange = (name: string, newValue: unknown): void => {
    const num = Number(newValue)
    const sanitized = !newValue || Number.isNaN(num) || num < 0 ? null : num
    setFilters({ ...filters, [name]: sanitized })
  }

  const blockNonPositiveIntKeys = (e: React.KeyboardEvent<HTMLInputElement>): void => {
    if (['-', '+', 'e', 'E', '.', ','].includes(e.key)) {
      e.preventDefault()
    }
  }

  const blockNegativeKeys = (e: React.KeyboardEvent<HTMLInputElement>): void => {
    if (['-', '+', 'e', 'E'].includes(e.key)) {
      e.preventDefault()
    }
  }

  const handleChangeBulk = (newValues: { [key: string]: unknown }): void => {
    setFilters({ ...filters, ...newValues })
  }

  const handleResetFilters = (): void => {
    setFilters(defaultFilters)
    setVisibility('all')
    setFiltersKey(uuid())
  }

  const handleSort = (selected: string) => {
    const splitSelected = selected.split('.')
    if (splitSelected.length === 4) {
      setSort({
        by: (splitSelected[0] + '.' + splitSelected[1]).toString() as sortFields,
        direction: splitSelected[2].toString() as 'asc' | 'desc',
        type: splitSelected[3] as sortTypes,
      })
      setSelectedSort(
        splitSelected[0] + '.' + splitSelected[1] + '.' + splitSelected[2] + '.' + splitSelected[3]
      )
    }
    if (splitSelected.length === 3) {
      setSort({
        by: splitSelected[0].toString() as sortFields,
        direction: splitSelected[1].toString() as 'asc' | 'desc',
        type: splitSelected[2] as sortTypes,
      })
      setSelectedSort(splitSelected[0] + '.' + splitSelected[1] + '.' + splitSelected[2])
    }
  }

  const anyYesNoOptions = [
    <MenuItem key={'any'} value={AnyYesNo.Any}>
      {t('filters.any')}
    </MenuItem>,
    <MenuItem key={'false'} value={AnyYesNo.No}>
      {t('filters.no')}
    </MenuItem>,
    <MenuItem key={'true'} value={AnyYesNo.Yes}>
      {t('filters.yes')}
    </MenuItem>,
  ]

  const handleChangeVisibility = (event: any) => {
    setVisibility(
      event.target.value as
        | 'all'
        | 'edited'
        | 'non-edited'
        | 'priceChanged'
        | 'priceUnchanged'
        | 'priceFailed'
    )
  }

  return (
    <div key={filtersKey} className="px-3 pt-2 pb-5 filter-dock-content">
      <Button
        size="small"
        variant="outlined"
        onClick={handleResetFilters}
        disabled={!areFiltersTouched}
        data-testid="reset-filters-btn"
        fullWidth
        color={'primary'}
        style={{ marginBottom: '1rem' }}
      >
        {areFiltersTouched ? t('filters.resetFilters') : t('filters.noActiveFilters')}
      </Button>

      {/* select for view all - view only edited etc  */}
      {parentPage !== PRICING_BOT ? (
        // this select must NOT be shown in the pricing bot results page
        <FormControl fullWidth>
          <InputLabel
            id="demo-simple-select-label"
            className={visibility !== 'all' ? `active-filter` : ``}
          >
            {t('filters.show')}
          </InputLabel>
          <Select
            fullWidth
            value={visibility}
            onChange={(e) => handleChangeVisibility(e)}
            className={visibility !== 'all' ? `active-filter` : ``}
          >
            <MenuItem value="all">View All</MenuItem>
            <MenuItem value="edited">Only Edited</MenuItem>
            <MenuItem value="non-edited">Only Non-Edited</MenuItem>
          </Select>
        </FormControl>
      ) : (
        <FormControl fullWidth>
          <InputLabel className={visibility !== 'all' ? `active-filter` : ``}>
            {t('filters.show')}
          </InputLabel>
          <Select
            fullWidth
            value={visibility}
            onChange={(e) => handleChangeVisibility(e)}
            className={visibility !== 'all' ? `active-filter` : ``}
          >
            {['all', 'priceChanged', 'priceUnchanged', 'priceFailed'].map((key: string) => {
              return (
                <MenuItem key={key} value={key}>
                  {t(`filters.${key}`)}
                </MenuItem>
              )
            })}
          </Select>
        </FormControl>
      )}

      <FormControl fullWidth className="mt-1">
        <InputLabel>Sort by</InputLabel>
        <Select
          fullWidth
          value={selectedSort}
          onChange={(e) => handleSort(e.target.value as string)}
          data-testid="sort-by"
        >
          {parentPage !== LISTING_AND_APPRAISAL
            ? sortOptions
                .slice(0, sortOptions.length - 2)
                .map(({ title, field, type, direction }) => (
                  <MenuItem key={field + direction} value={`${field}.${direction}.${type}`}>
                    {t(title)} {direction}
                  </MenuItem>
                ))
            : sortOptions.map(({ title, field, type, direction }) => (
                <MenuItem key={field + direction} value={`${field}.${direction}.${type}`}>
                  {t(title)} {direction}
                </MenuItem>
              ))}
        </Select>
      </FormControl>

      <Filter
        defaultValue={filters.name}
        setFilter={handleChange}
        label={activeStockType === 'singles' ? t('card.cardName') : t('card.itemName')}
        name="name"
      />

      <CustomTooltip
        placement={'top'}
        title={
          <div className="p-1">
            <Typography variant="body2" style={{ fontWeight: 'bold' }}>
              {'Locations Filter'}
            </Typography>{' '}
            <Typography variant="body2" style={{ fontWeight: 'normal' }}>
              {
                'Search by word in the locations. If you want to search by multiple words, separate them by pressing the enter key'
              }
            </Typography>{' '}
            <Typography variant="body2" style={{ fontWeight: 'normal' }}>
              {'Type "" to search for cards with no location.'}
            </Typography>{' '}
          </div>
        }
      >
        <div>
          <Filter
            defaultValue={filters.location}
            type="switchText"
            setFilter={handleChange}
            label={t('card.location')}
            name="location"
          />
        </div>
      </CustomTooltip>

      <div>
        <Filter
          defaultValue={filters.comment}
          type="switchText"
          setFilter={handleChange}
          label={t('card.comment')}
          name="comment"
        />
      </div>

      <Autocomplete<Expansion>
        options={expansionsActiveGame}
        renderOption={(option) => option.name}
        value={filters.expansion}
        autoHighlight
        className={filters.expansion ? `active-filter` : ''}
        getOptionLabel={(option) => `${option.name} ${option.code}`}
        onChange={async (_e, newValue) => {
          handleChange('expansion', newValue)
        }}
        data-testid="add-by-expansion-input"
        renderInput={(params) => (
          <TextField
            {...params}
            name="expansion"
            margin="dense"
            label={t('card.expansion')}
            type="text"
            className={filters.expansion ? `active-filter` : ''}
            fullWidth
          />
        )}
        getOptionSelected={(option, value) => option.name === value.name}
      />

      <Grid spacing={1} container style={{ padding: '3px', marginBottom: '8px' }}>
        <Filter
          grid
          defaultValue={filters.minQuantity}
          name="minQuantity"
          setFilter={handleQtyChange}
          label={t('filters.minQuantity')}
          inputProps={{
            type: 'number',
            onKeyDown: blockNonPositiveIntKeys,
            inputProps: { min: 1, step: 1 },
          }}
        />

        <Filter
          grid
          name="maxQuantity"
          defaultValue={filters.maxQuantity}
          setFilter={handleQtyChange}
          label={t('filters.maxQuantity')}
          inputProps={{
            type: 'number',
            onKeyDown: blockNonPositiveIntKeys,
            inputProps: { min: 1, step: 1 },
          }}
        />
      </Grid>

      {activeStockType === 'singles' && (
        <>
          <div style={{ padding: '3px' }}>
            <Filter
              type="slider"
              sliderProps={{
                valueLabelDisplay: 'auto',
                valueLabelFormat: (key: number) => CONDITIONS[key],
                step: 1,
                marks: true,
                min: 0,
                max: 6,
              }}
              defaultValue={filters.condition}
              name="condition"
              setFilter={handleChange}
              label={t('card.condition')}
              activeFilter={filters.condition[1] - filters.condition[0] !== 6}
            />
          </div>
          <div>
            <Filter
              type="slider"
              defaultValue={filters.rarity}
              sliderProps={{
                valueLabelDisplay: 'auto',
                valueLabelFormat: (key: number) => activeGame.rarities[key].filterLabel,
                step: 1,
                marks: true,
                min: 1,
                max: Object.keys(activeGame.rarities).length,
              }}
              name="rarity"
              setFilter={handleChange}
              label={t('card.rarity')}
              activeFilter={
                filters.rarity[0] !== 1 ||
                filters.rarity[1] !== Object.keys(activeGame.rarities).length
              }
            />
          </div>
        </>
      )}

      <Grid spacing={1} container style={{ padding: '3px' }}>
        <Filter
          grid
          defaultValue={filters.minPrice}
          name="minPrice"
          setFilter={handlePriceChange}
          label={t('filters.minPrice')}
          inputProps={{
            type: 'number',
            onKeyDown: blockNegativeKeys,
            inputProps: { min: 0, step: 0.01 },
          }}
        />

        <Filter
          grid
          name="maxPrice"
          defaultValue={filters.maxPrice}
          setFilter={handlePriceChange}
          label={t('filters.maxPrice')}
          inputProps={{
            type: 'number',
            onKeyDown: blockNegativeKeys,
            inputProps: { min: 0, step: 0.01 },
          }}
        />
      </Grid>

      <Grid spacing={1} container style={{ padding: '3px' }}>
        <Grid md={6} xs={12} item>
          <Filter
            type="select"
            menuitems={[
              <MenuItem key={'any'} value={'any'}>
                {t('filters.any')}
              </MenuItem>,

              ...CARD_LANGUAGES.map(({ value, label }) => (
                <MenuItem key={value} value={value}>
                  {label}
                </MenuItem>
              )),
            ]}
            setFilter={handleChange}
            defaultValue={filters.language}
            label={t('card.language')}
            name="language"
          />
        </Grid>

        {activeStockType === 'singles' &&
          activeGame.extraAttr.map((attribute) => {
            if (attribute !== CardExtraAttribute.Playset) {
              //isSigned to signed
              const parsedAttribute = (attribute[2].toLowerCase() +
                attribute.slice(3)) as keyof typeof filters
              return (
                <Grid md={6} xs={12} item key={attribute} className="extra-attribute">
                  <Filter
                    type="select"
                    menuitems={anyYesNoOptions}
                    setFilter={handleChange}
                    defaultValue={filters[parsedAttribute]}
                    label={t(`filters.${parsedAttribute}`)}
                    name={parsedAttribute}
                  />
                </Grid>
              )
            }
          })}

        <Grid md={6} xs={12} item className="finish-type">
          <Filter
            type="select"
            key="finishType"
            menuitems={[
              <MenuItem key={'any'} value={'any'}>
                {t('filters.any')}
              </MenuItem>,

              ...activeGame.finishType.map((finishType) => (
                <MenuItem key={finishType} value={finishType}>
                  {finishType}
                </MenuItem>
              )),
            ]}
            setFilter={handleChange}
            defaultValue={AnyYesNo.Any}
            label={t('filters.finishType')}
            name="finishType"
          />
        </Grid>

        {activeStockType === 'singles' && isStockPage && (
          <Grid md={6} xs={12} item>
            <Filter
              type="select"
              key="graded"
              menuitems={anyYesNoOptions}
              setFilter={handleChange}
              defaultValue={AnyYesNo.Any}
              label={t('filters.graded')}
              name="graded"
            />
          </Grid>
        )}

        {isStockPage && (
          <Grid md={6} xs={12} item>
            <Filter
              type="select"
              menuitems={anyYesNoOptions}
              setFilter={handleChange}
              defaultValue={filters.sold}
              label={t('filters.sold')}
              name="sold"
            />
          </Grid>
        )}

        {activeStockType === 'singles' && activeGame.idGame === IdGame.Yugioh && (
          <Grid md={6} xs={12} item>
            <Filter
              type="select"
              key="american"
              menuitems={anyYesNoOptions}
              setFilter={handleChange}
              defaultValue={AnyYesNo.Any}
              label={t('filters.americanEdition')}
              name="american"
            />
          </Grid>
        )}

        <Grid md={6} xs={12} item>
          <Filter
            type="select"
            menuitems={anyYesNoOptions}
            setFilter={handleChange}
            defaultValue={filters.selected}
            label={t('filters.selected')}
            name="selected"
          />
        </Grid>
      </Grid>
    </div>
  )
})
